import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE;

export default function PublicChat() {
  const { token } = useParams();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [invalid, setInvalid] = useState(false);

  const listRef = useRef(null);
  const pollRef = useRef(null);

  // mark dealer->buyer messages as read (buyer is viewing)
  const markRead = useCallback(async () => {
    if (document.hidden || invalid) return;
    try {
      await fetch(`${API}/public/chat/${token}/read`, { method: "POST" });
    } catch {}
  }, [API, token, invalid]);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API}/public/chat/${token}/messages`);
      if (res.status === 404 || res.status === 401) {
        setInvalid(true);
        setErr("This chat link is invalid or has expired.");
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      // 🔧 normalize server shapes -> {id, from, text, at}
      const mapped = (data || []).map((m) => ({
        id: m.id ?? m.Id ?? m.messageId ?? `tmp-${Math.random()}`,
        from: m.from ?? m.From ?? m.sender ?? m.Sender ?? "buyer",
        text: m.text ?? m.Text ?? m.body ?? m.Body ?? "",
        at: m.at ?? m.At ?? m.createdAt ?? m.CreatedAt ?? new Date().toISOString(),
        readAt: m.readAt ?? m.ReadAt ?? null,
      }));

      setMessages(mapped);

      // auto-scroll to bottom
      requestAnimationFrame(() => {
        listRef.current?.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: "smooth",
        });
      });
     // buyer is looking at the thread -> mark dealer msgs as read
      markRead();
    } catch (e) {
      if (!invalid) setErr("Could not load messages.");
    }
  };

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 7000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // also mark read when tab gains focus / becomes visible
  useEffect(() => {
    const onFocusOrVis = () => markRead();
    window.addEventListener("focus", onFocusOrVis);
    document.addEventListener("visibilitychange", onFocusOrVis);
    return () => {
      window.removeEventListener("focus", onFocusOrVis);
      document.removeEventListener("visibilitychange", onFocusOrVis);
    };
  }, [markRead]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    setErr("");

    const bodyText = text.trim();
    const nowIso = new Date().toISOString();

    // optimistic UI (already in normalized shape)
    const optimistic = {
      id: `tmp-${Date.now()}`,
      from: "buyer",
      text: bodyText,
      at: nowIso,
      readAt: null,
    };
    setMessages((prev) => [...prev, optimistic]);
    setText("");

    try {
      const res = await fetch(`${API}/public/chat/${token}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: bodyText }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // 🔧 If your POST returns only { id }, we still just refresh:
      // If you update backend to return full dto, you could merge instead.
      fetchMessages();
    } catch (e) {
      setErr("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-10 bg-white border-b">
        <div className="max-w-3xl mx-auto p-4">
          <h1 className="text-xl font-semibold">Chat with Dealer</h1>
          <p className="text-sm text-gray-500">
            {invalid
              ? "This link may be invalid or expired."
              : "You can reply here. No login required."}
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4">
        {err && (
          <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {err}
          </div>
        )}

        <div
          ref={listRef}
          className="bg-white border rounded-xl h-[60vh] overflow-y-auto p-3 space-y-2"
          aria-live="polite"
        >
          {messages.length === 0 && !invalid && (
            <div className="text-center text-gray-500 mt-6">
              No messages yet. Say hi!
            </div>
          )}

          {messages.map((m) => {
            const isDealer = m.from === "dealer";
            const at = new Date(m.at);
            const atStr = isNaN(at) ? "" : at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

            let status = "";
            if (!isDealer) { // only show ticks for buyer’s own messages
                status = m.readAt ? "✔✔ Read" : "✔ Sent";
            }

            return (
                <div key={m.id} className={`flex ${isDealer ? "justify-start" : "justify-end"}`} title={isNaN(at) ? "" : at.toLocaleString()}>
                <div className={`px-3 py-2 rounded-2xl max-w-[75%] ${isDealer ? "bg-gray-100 text-gray-900" : "bg-blue-600 text-white"}`}>
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    <div className="mt-1 text-[10px] opacity-75 text-right flex gap-2">
                    <span>{atStr}</span>
                    {!isDealer && <span>{status}</span>}
                    </div>
                </div>
                </div>
            );
            })}

        </div>

        {!invalid && (
          <form onSubmit={send} className="mt-3 flex gap-2">
            <input
              className="flex-1 h-11 rounded-lg border border-gray-300 px-3"
              placeholder="Type your message…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <button
              type="submit"
              disabled={sending || !text.trim()}
              className="h-11 px-4 rounded-lg bg-blue-600 text-white disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </form>
        )}

        <p className="text-xs text-gray-500 mt-2">
          This chat is linked to your inquiry. Keep this page bookmarked.
        </p>
      </main>
    </div>
  );
}
